import { z } from 'zod'
import { PRODUCT_CATEGORY_IDS } from '../constants'
import { menuTagsSchema, type MenuTags } from '../menu-tags'
import { AXES } from '../taxonomy'
import { searchQuerySchema } from './common'
import { CURRENCIES, LANGUAGES, SIMULATION_IMAGE_KEY_RE, type Language } from './simulation'

/** 글에 박히는 제품 스냅샷 (시뮬레이션 결과에서 뽑음) */
export const postProductSchema = z.object({
  name: z.string().min(1).max(80),
  tagline: z.string().max(140).default(''),
  suggestedRetail: z.number().nonnegative(),
  currency: z.enum(CURRENCIES),
  categoryId: z.enum(PRODUCT_CATEGORY_IDS),
  /** 대·중분류 (카드에는 이 둘 + 판매가만 보인다, 기기 언어 라벨은 taxonomyLabel 로). 옛 글은 null → categoryId 라벨 */
  axis: z.enum(AXES).default('product'),
  majorId: z.string().max(40).nullable().default(null),
  subId: z.string().max(80).nullable().default(null),
  /** 판매 단위 라벨 (예: "12 oz jar") */
  format: z.string().max(60).default(''),
  /** 결과 사진 R2 키 (GET /simulations/images/<key>). 옛 글·사진 없는 결과는 null → 카테고리 사진 */
  imageKey: z.string().nullable().default(null),
  /**
   * 1단위 원가 (결과의 costs.totalPerUnit, currency 단위). 올린 사람이 원가 공개(shareCost)를 켰고
   * 공개를 켜면 원가 완성 여부와 관계없이 싣는다 (2026-10-24, 대표 결정). 검증 전 추정값이라 앱은 '추정' 라벨을 단다.
   * 옛 글·공개 안 함·결과에 원가 칸이 없으면 칸이 없거나 null (2026-10-22, 대표 명세 3장 "제품 첨부 시 원가·마진 미니 표시")
   */
  unitCost: z.number().nonnegative().nullable().optional(),
  /** 마진율 0..1 (pricing.marginPct 와 같은 정의: (판매가 - 원가) / 판매가). unitCost 와 같은 조건 */
  marginPct: z.number().min(-1).max(1).nullable().optional(),
})
export type PostProduct = z.infer<typeof postProductSchema>

/**
 * 글 출처. user = 사용자가 쓴 글, example = 레시피캣 운영팀이 자주 받는 고민을 예시로 정리한 글 (시드).
 * 예시 글은 실제 사용자 글처럼 보이면 안 된다. 앱은 PostView.isExample 로 '예시 고민' 표시를 단다 (2026-10-22)
 */
export const POST_SOURCES = ['user', 'example'] as const
export type PostSource = (typeof POST_SOURCES)[number]

/**
 * 운영팀 예시 글인지 (api 응답 isExample, 관리자 표시가 같이 쓴다). 예전 시드(scripts/seed-community.ts)는
 * source 칸 없이 seed:* 사용자로 들어갔다. SQL 판정은 api lib/community-concerns.ts examplesOnlyIn 이 같은 식이다.
 * 앱은 서버가 준 isExample 을 본다 (apps/mobile 의 같은 이름 함수는 PostView 를 받는다)
 */
export function isExamplePost(p: { source?: PostSource | null; userId: string }): boolean {
  return p.source === 'example' || p.userId.startsWith('seed:')
}

/** 피드 정렬 (GET /community/posts sort). 앱 피드 질의와 같은 값 */
export const POST_SORTS = ['recent', 'popular'] as const
export type PostSort = (typeof POST_SORTS)[number]
/** 피드 기간 (GET /community/posts period) */
export const POST_PERIODS = ['all', 'week'] as const
export type PostPeriod = (typeof POST_PERIODS)[number]

/** POST /community/uploads 가 돌려주는 R2 키 형식. GET /community/images/<key> 로 서빙 */
export const POST_IMAGE_KEY_RE = /^post-uploads\/[^/]+\/[0-9a-f-]{36}\.(jpg|png|webp)$/
export const POST_MAX_IMAGES = 4

export const createPollSchema = z.object({
  title: z.string().max(60).default(''),
  options: z.array(z.string().min(1).max(60)).min(2).max(4),
})
export type CreatePollInput = z.infer<typeof createPollSchema>

/**
 * POST /community/posts — 하나의 글 모델 (2026-09-14 결정).
 * 제목(question) + 본문 + 사진 ≤4, 여기에 투표를 붙이면 투표 글. 투표는 내 시뮬레이션 결과(제품)가 필수,
 * 일반 글은 제품 첨부 선택.
 */
export const createPostSchema = z
  .object({
    /** 작성자 표시 이름 (Clerk 사용자에서) */
    authorName: z.string().min(1).max(60),
    /** 제목. 투표 글이면 질문 한 줄 */
    question: z.string().min(2).max(120),
    body: z.string().max(2000).default(''),
    images: z.array(z.string().regex(POST_IMAGE_KEY_RE)).max(POST_MAX_IMAGES).default([]),
    /** 첨부할 내 제품(완료된 시뮬레이션). 투표가 있으면 필수 */
    simulationId: z.string().min(1).nullable().default(null),
    poll: createPollSchema.nullable().default(null),
    /** 작성자 기기 언어 — 같은 언어 사용자에게만 노출 */
    language: z.enum(LANGUAGES).default('en'),
    /**
     * 고민 유형 id (concern-tags.ts, 선택). 서버가 normalizeConcernTags 로 모르는 값을 버리고 3개까지만 남긴다.
     * 아래 새 칸은 모두 선택이다. 옛 앱(1.0.3·1.0.4)은 보내지 않는다 (2026-10-22)
     */
    concernTags: z.array(z.string().min(1).max(40)).max(10).optional(),
    /** 글의 메뉴 꼬리표 (선택). 비어 있고 제품을 붙였으면 서버가 그 시뮬레이션의 menuTags 를 복사 */
    menuTags: menuTagsSchema.optional(),
    /** 붙인 제품의 원가·마진을 카드에 보일지. 사장님 원가는 민감 정보라 기본은 끔 */
    shareCost: z.boolean().optional(),
  })
  .refine((v) => !v.poll || v.simulationId, {
    message: 'A poll needs one of your products',
    path: ['simulationId'],
  })
export type CreatePostInput = z.infer<typeof createPostSchema>

/**
 * PATCH /community/posts/:id — 제목·본문 (사진·투표는 그대로).
 * 고민 유형·메뉴 꼬리표는 보냈을 때만 바꾼다. 옛 앱이 제목만 고쳐도 꼬리표가 지워지지 않게 default 를 두지 않는다
 */
export const updatePostSchema = z.object({
  question: z.string().min(2).max(120),
  body: z.string().max(2000).default(''),
  concernTags: z.array(z.string().min(1).max(40)).max(10).optional(),
  menuTags: menuTagsSchema.optional(),
})
export type UpdatePostInput = z.infer<typeof updatePostSchema>

export const voteSchema = z.object({ optionId: z.string().min(1) })

export const postListQuerySchema = z.object({
  categoryId: z.enum(PRODUCT_CATEGORY_IDS).optional(),
  /** 요청자 언어 — 이 언어로 올라온 글만 (없으면 전체) */
  lang: z.enum(LANGUAGES).optional(),
  /**
   * lang 이 없을 때 예시 글(source=example, 예전 시드 포함)은 이 언어 것만. 사용자 글은 언어와 관계없이 그대로.
   * 같은 예시 고민이 4개 언어로 들어가도 '전체 언어' 피드에 한 번만 보이게 (2026-10-23, CS2-05).
   * 새 앱(1.0.5~)이 앱 언어를 싣는다. 옛 앱(1.0.3·1.0.4)은 보내지 않으므로 서버가 Accept-Language 로 한 언어를 고른다 (없으면 ko, 2026-10-24)
   */
  exampleLang: z.enum(LANGUAGES).optional(),
  /** 내 글만 */
  mine: z.coerce.boolean().optional(),
  /** 어드민이 추천한 글만 (홈 상단). 최근 추천순 */
  featured: z.coerce.boolean().optional(),
  /** 검색어 — 제목·본문 부분 일치. 60자를 넘으면 앞 60자로 (searchQuerySchema) */
  q: searchQuerySchema.optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  /** 이전 페이지 마지막 항목의 createdAt (ISO). 그보다 오래된 것부터. 인기순이면 무시 */
  cursor: z.string().datetime().optional(),
  /** 고민 유형 하나 (concern-tags.ts 의 id) */
  concern: z.string().max(40).optional(),
  /** 메뉴 꼬리표 하나, 'axis:value' 꼴 (예: 'technique:sous-vide'). ':' 가 없으면 고민 유형 id 로 본다 */
  tag: z.string().max(60).optional(),
  /** recent = 최신순, popular = 실제 반응(댓글 + 투표) 많은 순. 반응이 같으면 운영팀 추천, 그다음 최신 */
  sort: z.enum(POST_SORTS).default('recent'),
  /** week = 최근 7일에 올라온 글만 */
  period: z.enum(POST_PERIODS).default('all'),
  /** 이 묶음(make = 제조사 고민, menu = 메뉴 속성 고민)의 고민 유형이 하나라도 있는 글만 */
  group: z.enum(['make', 'menu']).optional(),
  /**
   * 'true' 면 투표가 붙은 글만 (2026-11-01, 커뮤니티 '투표' 칩). 'true' 만 받는다 (coerce.boolean 은 'false' 도 참이라서).
   * 옛 앱은 보내지 않는다
   */
  polls: z.enum(['true']).optional(),
})
export type PostListQuery = z.infer<typeof postListQuerySchema>

/** 인기순 정렬의 근거. reactions = 실제 반응, featured = 반응이 없어 운영팀 추천으로 대신, recent = 둘 다 없어 최신순 */
export const POST_RANK_BASES = ['reactions', 'featured', 'recent'] as const
export type PostRankBasis = (typeof POST_RANK_BASES)[number]

/**
 * GET /community/similar: 이 글(postId) 또는 내 시뮬레이션(simulationId)과 비슷한 고민. 응답 { items: PostView[] }.
 * 둘 중 정확히 하나만. GET /community/posts/:id/similar 는 postId 를 경로로 받는 같은 기능
 */
export const similarPostsQuerySchema = z
  .object({
    postId: z.string().min(1).optional(),
    simulationId: z.string().min(1).optional(),
    /** 같은 언어 글만 (없으면 기준 글의 언어, 시뮬레이션 기준이면 전체) */
    lang: z.enum(LANGUAGES).optional(),
    limit: z.coerce.number().int().min(1).max(20).default(6),
  })
  .refine((v) => Boolean(v.postId) !== Boolean(v.simulationId), {
    message: 'postId or simulationId',
  })
export type SimilarPostsQuery = z.infer<typeof similarPostsQuerySchema>

/** GET /community/posts/:id/similar */
export const similarPostsByIdQuerySchema = z.object({
  lang: z.enum(LANGUAGES).optional(),
  limit: z.coerce.number().int().min(1).max(20).default(6),
})

/**
 * GET /community/best/weekly: 이번 주 고민 베스트 (제조사 고민 중심).
 * 응답 { items: PostView[], source: 'reactions' | 'featured', since: ISO }. 반응이 있는 글이 없으면 source 'featured'
 */
export const weeklyBestQuerySchema = z.object({
  lang: z.enum(LANGUAGES).optional(),
  /** lang 이 없을 때 예시 글은 이 언어 것만 (postListQuerySchema 의 exampleLang 과 같다) */
  exampleLang: z.enum(LANGUAGES).optional(),
  limit: z.coerce.number().int().min(1).max(10).default(5),
})
export const WEEKLY_BEST_SOURCES = ['reactions', 'featured'] as const
export type WeeklyBestSource = (typeof WEEKLY_BEST_SOURCES)[number]

/**
 * 한 번 누르는 가벼운 반응 (2026-11-01 대표 요청). 좋아요보다 가볍게, 글마다 사람당 종류별 하나.
 * same = '나도 같은 고민', tried = '이 방향으로 해봤어요'. 글쓴이 본인은 누를 수 없다 (서버가 400)
 */
export const POST_REACTIONS = ['same', 'tried'] as const
export type PostReaction = (typeof POST_REACTIONS)[number]

/** POST /community/posts/:id/reactions — on=true 면 켜고 false 면 끈다 (여러 번 보내도 같은 결과). 응답 { post } */
export const postReactionSchema = z.object({
  kind: z.enum(POST_REACTIONS),
  on: z.boolean(),
})
export type PostReactionInput = z.infer<typeof postReactionSchema>

/** 반응 종류별 수. 옛 서버 응답에는 없다 */
export type PostReactionCounts = Record<PostReaction, number>

/**
 * GET /community/best: 오늘의 고민(day) · 이번 주 핫한 고민(week) · 이번 달 베스트(month) (2026-11-01).
 * 그 기간에 생긴 실제 활동(표·댓글·반응·조회)만 센다. 활동이 모인 글이 기간별 최소 개수(BEST_MIN_ACTIVE)보다 적으면
 * 최신 글로 대신하고 source 'recent' 로 알린다 (앱은 '새로 올라온 고민' 이라고 적는다). 응답 BestResponse
 */
export const BEST_PERIODS = ['day', 'week', 'month'] as const
export type BestPeriod = (typeof BEST_PERIODS)[number]
/**
 * 활동이 모인 글을 세우는 순서. activity = 활동 점수 순(기본), views = 그 기간에 연 사람 수 순 (2026-11-01 주간 베스트 화면).
 * 어느 쪽이든 '활동이 모인 글' 판정(BEST_MIN_SCORE·BEST_MIN_ACTIVE)은 같다. 최신 글로 대신할 때(source recent)는 최신순 그대로
 */
export const BEST_SORTS = ['activity', 'views'] as const
export type BestSort = (typeof BEST_SORTS)[number]
export const bestQuerySchema = z.object({
  period: z.enum(BEST_PERIODS).default('week'),
  lang: z.enum(LANGUAGES).optional(),
  /** lang 이 없을 때 예시 글은 이 언어 것만 (postListQuerySchema 의 exampleLang 과 같다) */
  exampleLang: z.enum(LANGUAGES).optional(),
  limit: z.coerce.number().int().min(1).max(10).default(5),
  /** 고민 유형 하나 (커뮤니티 고민 탭, postListQuerySchema 의 concern 과 같다). 옛 앱은 보내지 않는다 */
  concern: z.string().max(40).optional(),
  sort: z.enum(BEST_SORTS).default('activity'),
})
export type BestQuery = z.infer<typeof bestQuerySchema>

/** activity = 실제 활동 순, recent = 활동이 모자라 최신 글 순 */
export const BEST_SOURCES = ['activity', 'recent'] as const
export type BestSource = (typeof BEST_SOURCES)[number]

/**
 * 기간별 '활동이 모였다' 고 부를 최소 글 수. 이보다 적으면 순위를 세우지 않고 최신 글로 대신한다.
 * 오늘의 고민은 한 글이라 1, 주·월 베스트는 서너 글이 겨뤄야 베스트라 3
 */
export const BEST_MIN_ACTIVE: Record<BestPeriod, number> = { day: 1, week: 3, month: 3 }

/**
 * 활동 점수 = 3 × (표 + 댓글 + 반응) + 조회한 사람 수. 반응 하나가 조회 셋과 같다.
 * 이 점수가 BEST_MIN_SCORE 이상인 글만 '활동이 모인 글' (반응 하나, 또는 다른 사람 셋이 열어 봄).
 * 시드 계정(seed:*)과 글쓴이 본인의 활동은 세지 않는다
 */
export const BEST_REACTION_POINTS = 3
export const BEST_MIN_SCORE = 3

export interface BestResponse {
  items: PostView[]
  source: BestSource
  period: BestPeriod
  /** 활동을 센 기간의 시작 (ISO) */
  since: string
  /**
   * 받은 정렬 (2026-11-01 주간 베스트 화면에서 더함). 옛 서버 응답에는 없다 — 앱은 그때 activity 로 보고,
   * 조회수순을 골랐어도 '반응' 기준으로 적는다
   */
  sort?: BestSort
  /** 받은 고민 유형 거르기 (없으면 null). 옛 서버 응답에는 없다 — 앱은 그때 글의 고민 유형으로 직접 거른다 */
  concern?: string | null
}

/** PATCH /admin/community/posts/:id — 홈 추천 토글 · 언어 재지정 (기기 언어와 다른 언어로 쓴 글 교정) */
export const adminPostPatchSchema = z.object({
  featured: z.boolean().optional(),
  language: z.enum(LANGUAGES).optional(),
})
export type AdminPostPatch = z.infer<typeof adminPostPatchSchema>

/**
 * 글 내용의 문자로 언어 판정. 기기 언어(en)로 쓴 한국어 글이 영어 피드에 새는 걸 막는다 (2026-09-14).
 * 한글 → ko, 가나 → ja, 한자만 → zh, 그 외(라틴 등) → null (호출 측이 기기 언어로).
 * 한자는 일본어에도 쓰이므로 가나가 한 글자라도 있으면 ja.
 */
export function detectContentLanguage(text: string): Language | null {
  if (/[\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF]/.test(text)) return 'ko'
  if (/[\u3040-\u30FF]/.test(text)) return 'ja'
  if (/[\u4E00-\u9FFF]/.test(text)) return 'zh'
  return null
}

/** POST /community/posts/:id/comments — 플랫 댓글 (대댓글 없음, 2026-09-14 결정) */
export const createCommentSchema = z.object({
  authorName: z.string().min(1).max(60),
  body: z.string().trim().min(1).max(1000),
})
export type CreateCommentInput = z.infer<typeof createCommentSchema>

export const commentListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(30),
  /** 이전 페이지 마지막 댓글의 createdAt (ISO). 오래된 순이라 그보다 새 것부터 */
  cursor: z.string().datetime().optional(),
})

/**
 * 신고 사유 (App Store 1.2 — 사용자 생성 콘텐츠 신고 경로, 2026-09-29).
 * spam = 스팸·광고·사기, harassment = 괴롭힘·혐오·모욕, sexual = 음란물, violence = 폭력·위험, other = 기타
 */
export const REPORT_REASONS = ['spam', 'harassment', 'sexual', 'violence', 'other'] as const
export type ReportReason = (typeof REPORT_REASONS)[number]

/**
 * POST /community/posts/:id/report · POST /community/comments/:id/report.
 * 운영자에게 알림만 보낸다 (DB 기록 없음). 차단 목록은 기기(AsyncStorage)에 있고,
 * 차단할 때도 이 경로로 blocked=true 를 보내 운영자가 문제 콘텐츠를 알게 한다.
 */
export const reportContentSchema = z.object({
  reason: z.enum(REPORT_REASONS),
  /** 선택 메모 */
  note: z.string().trim().max(500).optional(),
  /** 신고자가 작성자를 차단했는지 */
  blocked: z.boolean().default(false),
})
export type ReportContentInput = z.infer<typeof reportContentSchema>

/** 신고한 AI 결과 부분 — result = 결과 화면(제품 글·재료·단계·AI 사진), edit = AI 수정 대화의 답 (또는 저장 전 미리보기) */
export const SIMULATION_REPORT_PARTS = ['result', 'edit'] as const
export type SimulationReportPart = (typeof SIMULATION_REPORT_PARTS)[number]

/**
 * POST /simulations/:id/report — AI 생성 결과 신고 (Google Play AI 생성 콘텐츠 정책, 2026-09-29). 게스트도.
 * 운영자에게 알리고 admin_audit 에 남긴다. AI 결과는 사용자 것이라 숨기지 않는다.
 */
export const reportSimulationSchema = z.object({
  reason: z.enum(REPORT_REASONS),
  part: z.enum(SIMULATION_REPORT_PARTS).default('result'),
  /** 신고할 때 보고 있던 결과 사진 */
  imageKey: z.string().regex(SIMULATION_IMAGE_KEY_RE).optional(),
  /** edit: 신고한 AI 답 원문 — 서버에 저장되지 않는 글이라 앱이 보낸다 */
  excerpt: z.string().trim().min(1).max(2000).optional(),
})
export type ReportSimulationInput = z.infer<typeof reportSimulationSchema>

/** 피드 항목 (앱·어드민 공용) */
export interface PostOptionView {
  id: string
  label: string
  votes: number
}
export interface PostPollView {
  title: string
  options: PostOptionView[]
  totalVotes: number
  /** 로그인 사용자가 고른 선택지 (없으면 null) */
  myOptionId: string | null
}
export interface PostView {
  id: string
  userId: string
  authorName: string
  /** 제목 (투표 글이면 질문) */
  question: string
  body: string
  /** R2 키 — GET /community/images/<key> */
  images: string[]
  language: string
  simulationId: string | null
  categoryId: string | null
  product: PostProduct | null
  poll: PostPollView | null
  commentCount: number
  /** 어드민 추천 (홈 상단) */
  featured: boolean
  /**
   * 같은 샘플번호의 완성 사례 id (2026-10-22). 사례가 살아 있고(내림·숨김 아님) 설정 sampleCases.enabled 가 켜졌을 때만.
   * 새 앱은 글 상세에 '이 사례로 시작하기'를 그린다. 옛 앱은 이 칸을 무시한다
   */
  caseId?: string | null
  /**
   * 그 사례로 지금 시작할 수 있나 (2026-10-22, 반박 검토 M1). 사례 시작 허용, 설정 startEnabled, 분류표 확인을 모두 통과하면 참.
   * caseId 가 null 이면 false. 새 앱은 거짓이면 글 상세 버튼을 '완성 사례 보기'로 바꾼다
   */
  caseStartable?: boolean
  createdAt: string
  /** 고민 유형 id (최대 3). 옛 글은 [] */
  concernTags: string[]
  /** 글의 메뉴 꼬리표. 옛 글은 {} */
  menuTags: MenuTags
  /** 레시피캣 운영팀의 예시 고민 (posts.source = 'example'). 앱은 '예시 고민' 표시를 단다 */
  isExample: boolean
  /**
   * 원가·마진이 운영팀 예시 수치인가 (예시 글이면 참, 2026-11-01). '예시 고민' 배지(isExample)를 내려도 숫자 옆 '예시 수치'는 남긴다.
   * 옛 서버 응답에는 없다 (앱은 그때 isExample 로 본다). 옛 앱은 이 칸을 무시한다
   */
  exampleFigures?: boolean
  /**
   * 인기순·주간 베스트에서만: 순위에 쓴 실제 반응 점수. 시드 계정(seed:*)·글쓴이 본인의 표·댓글·반응은 빼고 센다.
   * 인기순은 표 + 댓글 + 반응 수, 주간 베스트는 최근 7일 반응 수에 제조사 고민 가중(2배)을 곱한 값
   */
  reactionScore?: number
  /**
   * '나도 같은 고민' · '이 방향으로 해봤어요' 수 (2026-11-01). 시드 계정(seed:*) 반응은 세지 않는다.
   * 옛 서버 응답에는 없다 (앱은 없으면 0 으로 본다). 옛 앱은 이 칸을 무시한다
   */
  reactions?: PostReactionCounts
  /** 로그인 사용자가 누른 반응 (게스트·옛 서버는 빈 배열 또는 없음) */
  myReactions?: PostReaction[]
  /**
   * GET /community/best 에서만: 그 기간의 실제 활동 수 (표 + 댓글 + 반응, 조회 제외). source 가 recent 면 0 일 수 있다.
   * 앱은 '반응 N' 으로 보인다
   */
  activityCount?: number
  /**
   * GET /community/best 에서만 (2026-11-01 주간 베스트 화면): 그 기간에 글을 연 사람 수. 사람당 한 번, 시드 계정·글쓴이 본인 제외.
   * 옛 서버 응답에는 없다 (앱은 조회 수를 보이지 않는다)
   */
  viewCount?: number
}
export interface CommentView {
  id: string
  postId: string
  userId: string
  authorName: string
  body: string
  createdAt: string
}
